import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import {
    MagnifyingGlassIcon,
    CalendarIcon,
    ClockIcon,
    EyeIcon,
    HeartIcon,
    ShareIcon,
    TagIcon,
    UserIcon,
    PhotoIcon
} from '@heroicons/react/24/outline'
import { blogAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'

const Blogs = () => {
    const [filters, setFilters] = useState({
        search: '',
        category: '',
        page: 1,
        limit: 12,
        sort: 'newest'
    })

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['blogs', filters],
        queryFn: () => blogAPI.getBlogs(filters),
        keepPreviousData: true,
    })

    const { data: categoriesData } = useQuery({
        queryKey: ['blog-categories'],
        queryFn: () => blogAPI.getCategories(),
    })

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }))
    }

    const handleSearch = (e) => {
        e.preventDefault()
        refetch()
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    const truncateText = (text, maxLength) => {
        if (text.length <= maxLength) return text
        return text.substr(0, maxLength) + '...'
    }

    if (isLoading) return <LoadingSpinner />
    if (error) return <ErrorMessage message="Failed to load blogs" />

    const blogs = data?.data?.blogs || []
    const pagination = data?.data?.pagination || {}
    const categories = categoriesData?.data?.categories || []

    return (
        <div className="min-h-screen bg-gray-50 pt-0">
            <Helmet>
                <title>Blogs - Education News & Insights | Margdarshak</title>
                <meta name="description" content="Stay updated with the latest education news, career guidance, study tips, and industry insights from our expert blog." />
                <meta name="keywords" content="education blog, career guidance, study tips, university news, admission process, scholarships, exam preparation" />
            </Helmet>

            {/* Premium Light Theme Blog Hero Section */}
            <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-32 overflow-hidden bg-white border-b border-gray-200 mt-0">
                {/* Dynamic Mesh Gradient Background - Subtle for Light Theme */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary-100 blur-[120px] animate-pulse"></div>
                    <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-100 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                </div>

                {/* Subtle Overlay Grid */}
                <div className="absolute inset-0 z-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

                <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center space-x-2 bg-primary-50 border border-primary-100 px-4 py-2 rounded-full mb-8"
                        >
                            <span className="w-2 h-2 bg-primary-400 rounded-full animate-ping"></span>
                            <span className="text-sm font-black text-primary-700 uppercase tracking-widest">Insights & Updates</span>
                        </motion.div>

                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-5xl sm:text-7xl lg:text-8xl font-black mb-8 text-slate-900 leading-tight tracking-tight"
                        >
                            Education <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-indigo-600">Blog Center</span>
                        </motion.h1>

                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="text-lg sm:text-xl text-slate-600 mb-12 max-w-2xl mx-auto font-medium"
                        >
                            Stay updated with the latest education news, career guidance, and industry insights from our experts.
                        </motion.p>

                        {/* Integrated Search Bar - Light Theme Glassmorphism */}
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="max-w-2xl mx-auto"
                        >
                            <form onSubmit={handleSearch} className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-primary-400 to-indigo-400 rounded-2xl blur opacity-20 group-focus-within:opacity-40 transition duration-1000"></div>
                                <div className="relative flex flex-col md:flex-row gap-4 bg-white/80 backdrop-blur-xl border border-slate-200 p-2 rounded-2xl shadow-xl shadow-slate-200/50">
                                    <div className="relative flex-1">
                                        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400 group-focus-within:text-primary-600 transition-colors" />
                                        <input
                                            type="text"
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                            placeholder="Search articles..."
                                            className="w-full pl-12 pr-4 py-4 bg-transparent text-slate-900 border-none focus:ring-0 text-lg placeholder:text-slate-400"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold transition-all transform hover:scale-105"
                                    >
                                        Search
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </div>
            </section>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Filters */}
                    <div className="lg:w-1/4">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">Filters</h3>

                            {/* Category Filter */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">Category</label>
                                <select
                                    value={filters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="">All Categories</option>
                                    {categories.map(category => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Sort Filter */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">Sort By</label>
                                <select
                                    value={filters.sort}
                                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="popular">Most Popular</option>
                                    <option value="views">Most Viewed</option>
                                </select>
                            </div>

                            {/* Categories List */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Popular Categories</h4>
                                <div className="space-y-2">
                                    {categories.slice(0, 8).map(category => (
                                        <button
                                            key={category}
                                            onClick={() => handleFilterChange('category', category)}
                                            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${filters.category === category
                                                ? 'bg-primary-100 text-primary-700'
                                                : 'text-gray-600 hover:bg-gray-100'
                                                }`}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="lg:w-3/4">
                        {/* Results Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {filters.search ? `Search Results for "${filters.search}"` : 'All Blogs'}
                                </h2>
                                <p className="text-gray-600 mt-1">
                                    {pagination.total_count || 0} blogs found
                                </p>
                            </div>
                            {filters.category && (
                                <button
                                    onClick={() => handleFilterChange('category', '')}
                                    className="text-sm text-primary-600 hover:text-primary-700"
                                >
                                    Clear Category Filter
                                </button>
                            )}
                        </div>

                        {/* Blogs Grid */}
                        {blogs.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                {blogs.map((blog, index) => (
                                    <motion.article
                                        key={blog._id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                                    >
                                        {/* Featured Image */}
                                        <div className="aspect-video bg-gray-200">
                                            {blog.featured_image ? (
                                                <img
                                                    src={blog.featured_image}
                                                    alt={blog.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <PhotoIcon className="h-12 w-12 text-gray-400" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-6">
                                            {/* Category & Featured Badge */}
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {blog.category}
                                                </span>
                                                {blog.featured && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        Featured
                                                    </span>
                                                )}
                                            </div>

                                            {/* Title */}
                                            <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                                                <Link
                                                    to={`/blogs/${blog.slug}`}
                                                    className="hover:text-primary-600 transition-colors"
                                                >
                                                    {blog.title}
                                                </Link>
                                            </h3>

                                            {/* Excerpt */}
                                            <p className="text-gray-600 mb-4 line-clamp-3">
                                                {truncateText(blog.excerpt, 150)}
                                            </p>

                                            {/* Tags */}
                                            {blog.tags && blog.tags.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-4">
                                                    {blog.tags.slice(0, 3).map((tag, tagIndex) => (
                                                        <span
                                                            key={tagIndex}
                                                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                                                        >
                                                            <TagIcon className="h-3 w-3 mr-1" />
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Meta Information */}
                                            <div className="flex items-center justify-between text-sm text-gray-500">
                                                <div className="flex items-center space-x-4">
                                                    <div className="flex items-center">
                                                        <UserIcon className="h-4 w-4 mr-1" />
                                                        {blog.created_by?.name || 'Admin'}
                                                    </div>
                                                    <div className="flex items-center">
                                                        <CalendarIcon className="h-4 w-4 mr-1" />
                                                        {formatDate(blog.published_at || blog.createdAt)}
                                                    </div>
                                                    <div className="flex items-center">
                                                        <ClockIcon className="h-4 w-4 mr-1" />
                                                        {blog.reading_time} min read
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex items-center">
                                                        <EyeIcon className="h-4 w-4 mr-1" />
                                                        {blog.views || 0}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.article>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="text-gray-400 mb-4">
                                    <PhotoIcon className="h-16 w-16 mx-auto" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No blogs found</h3>
                                <p className="text-gray-600">
                                    {filters.search || filters.category
                                        ? 'Try adjusting your search criteria or filters.'
                                        : 'No blogs have been published yet.'}
                                </p>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                                    {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of{' '}
                                    {pagination.total_count} results
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                                        disabled={pagination.current_page <= 1}
                                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg">
                                        {pagination.current_page}
                                    </span>
                                    <button
                                        onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                                        disabled={pagination.current_page >= pagination.total_pages}
                                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Blogs
